<template>
  <div>
    <!-- AI Chat Drawer -->
    <v-navigation-drawer
      :value="value"
      @input="$emit('input', $event)"
      right
      app
      width="420"
      color="white"
      class="ai-chat-drawer elevation-8"
      :overlay="$vuetify.breakpoint.mobile"
    >
      <!-- Chat Header -->
      <div class="chat-header pa-4 grey lighten-5">
        <div class="d-flex align-center">
          <v-avatar size="36" color="grey darken-3" class="mr-3">
            <v-icon color="white" size="20">mdi-robot-outline</v-icon>
          </v-avatar>
          <div>
            <div class="text-subtitle-2 font-weight-medium grey--text text--darken-3">
              AI Assistant
            </div>
            <div class="text-caption grey--text text--darken-1">
              Online • Ready to help
            </div>
          </div>
          <v-spacer></v-spacer>
          <v-btn icon small @click="$emit('toggle')" color="grey darken-1">
            <v-icon size="20">mdi-close</v-icon>
          </v-btn>
        </div>
      </div>

      <!-- Chat Messages -->
      <div class="chat-messages-container" ref="chatMessages">
        <!-- Welcome State -->
        <div v-if="messages.length === 0" class="welcome-chat pa-6">
          <div class="text-center">
            <v-avatar size="80" color="grey lighten-4" class="mb-6">
              <v-icon size="40" color="grey darken-2">mdi-robot-outline</v-icon>
            </v-avatar>
            <h3 class="text-h6 font-weight-regular grey--text text--darken-3 mb-3">
              Hello! I'm your AI assistant
            </h3>
            <p class="text-body-2 grey--text text--darken-1 mb-6">
              I can help you with tickets, answer questions, or provide support
              information.
            </p>

            <!-- Quick Actions -->
            <div class="quick-actions">
              <v-btn
                v-for="action in quickActions"
                :key="action.text"
                text
                small
                color="grey darken-2"
                @click="sendQuickAction(action.text)"
                class="ma-1 text-none"
              >
                <v-icon left size="16">{{ action.icon }}</v-icon>
                {{ action.label }}
              </v-btn>
            </div>
          </div>
        </div>

        <!-- Messages List -->
        <div v-else class="messages-list pa-4">
          <div v-for="(msg, index) in messages" :key="index" class="message-item mb-4">
            <div
              class="d-flex"
              :class="msg.role === 'user' ? 'justify-end' : 'justify-start'"
            >
              <v-avatar
                v-if="msg.role === 'assistant'"
                size="28"
                color="grey darken-3"
                class="mr-2 mt-1"
              >
                <v-icon color="white" size="14">mdi-robot-outline</v-icon>
              </v-avatar>

              <div class="message-bubble" :class="msg.role">
                <div v-html="formatMessage(msg.content)"></div>
                <div class="message-time mt-2">
                  {{ formatMessageTime(msg.timestamp) }}
                </div>
              </div>

              <v-avatar
                v-if="msg.role === 'user'"
                size="28"
                color="grey lighten-2"
                class="ml-2 mt-1"
              >
                <v-icon color="grey darken-2" size="14">mdi-account-outline</v-icon>
              </v-avatar>
            </div>
          </div>

          <!-- Typing Indicator -->
          <div v-if="isTyping" class="message-item mb-4">
            <div class="d-flex justify-start">
              <v-avatar size="28" color="grey darken-3" class="mr-2 mt-1">
                <v-icon color="white" size="14">mdi-robot-outline</v-icon>
              </v-avatar>
              <div class="message-bubble typing-bubble">
                <div class="typing-dots">
                  <span></span>
                  <span></span>
                  <span></span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Chat Input -->
      <div class="chat-input-area pa-4 grey lighten-5">
        <v-text-field
          v-model="newMessage"
          placeholder="Type your message..."
          outlined
          dense
          hide-details
          color="grey darken-3"
          background-color="white"
          @keyup.enter="sendMessage"
          :disabled="isTyping"
          class="chat-input-field"
        >
          <template v-slot:append>
            <v-btn
              icon
              small
              color="grey darken-3"
              @click="sendMessage"
              :disabled="!newMessage.trim() || isTyping"
            >
              <v-icon size="18">mdi-send</v-icon>
            </v-btn>
          </template>
        </v-text-field>
        <div class="text-center mt-3">
          <span class="text-caption grey--text text--darken-1">
            Powered by Claude AI • Responses are AI-generated
          </span>
        </div>
      </div>
    </v-navigation-drawer>

    <!-- Floating AI Chat Button -->
    <v-btn
      v-if="!value && isAuthenticated"
      fab
      fixed
      bottom
      right
      color="grey darken-3"
      @click="$emit('toggle')"
      class="floating-chat-btn elevation-8"
      :style="{ marginRight: '24px', marginBottom: '24px' }"
    >
      <v-icon color="white">mdi-robot-outline</v-icon>
    </v-btn>
  </div>
</template>

<script>
export default {
  name: "AIChat",
  props: {
    value: {
      type: Boolean,
      default: false,
    },
    apiUrl: {
      type: String,
      required: true,
    },
    currentUser: {
      type: Object,
      default: null,
    },
    isAuthenticated: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      // Chat Data
      messages: [],
      newMessage: "",
      isTyping: false,
      conversationId: null,

      quickActions: [
        {
          icon: "mdi-help-circle-outline",
          label: "FAQ",
          text: "Show me frequently asked questions",
        },
        { icon: "mdi-ticket-outline", label: "My Tickets", text: "Show my tickets" },
        { icon: "mdi-tools", label: "Support", text: "I need technical help" },
      ],
    };
  },

  computed: {
    authToken() {
      return localStorage.getItem("support_token");
    },
  },

  watch: {
    value(newVal) {
      if (newVal) {
        this.$nextTick(() => {
          this.scrollToBottom();
        });
      }
    },
  },

  methods: {
    sendQuickAction(text) {
      this.newMessage = text;
      this.sendMessage();
    },

    async sendMessage() {
      if (!this.newMessage.trim()) return;

      const userMessage = {
        role: "user",
        content: this.newMessage,
        timestamp: new Date(),
      };
      this.messages.push(userMessage);

      const messageContent = this.newMessage;
      this.newMessage = "";
      this.isTyping = true;

      this.$nextTick(() => {
        this.scrollToBottom();
      });

      try {
        let finalMessage = messageContent;

        if (!this.conversationId && this.isAuthenticated && this.currentUser) {
          finalMessage = `User: ${this.currentUser.name} (${this.currentUser.email})\nQuestion: ${messageContent}`;
        }

        const response = await this.makeApiCall(`${this.apiUrl}/claude-agent/message`, {
          method: "POST",
          body: JSON.stringify({
            content: finalMessage,
            conversation_id: this.conversationId,
          }),
        });

        if (!this.conversationId && response.conversation_id) {
          this.conversationId = response.conversation_id;
        }

        this.messages.push({
          role: "assistant",
          content: response.message,
          timestamp: new Date(),
        });
      } catch (err) {
        this.messages.push({
          role: "assistant",
          content: "Sorry, there was an error processing your message. Please try again.",
          timestamp: new Date(),
        });
      } finally {
        this.isTyping = false;
        this.$nextTick(() => {
          this.scrollToBottom();
        });
      }
    },

    scrollToBottom() {
      const container = this.$refs.chatMessages;
      if (container) {
        container.scrollTop = container.scrollHeight;
      }
    },

    // API Helper
    async makeApiCall(url, options = {}) {
      const response = await fetch(url, {
        ...options,
        headers: {
          "Content-Type": "application/json",
          ...options.headers,
          Authorization: this.authToken,
        },
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `Error ${response.status}`);
      }

      return await response.json();
    },

    formatMessageTime(timestamp) {
      if (!timestamp) return "";
      return new Date(timestamp).toLocaleTimeString("en-US", {
        hour: "2-digit",
        minute: "2-digit",
      });
    },

    formatMessage(text) {
      if (!text) return "";

      let formatted = text
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;");

      formatted = formatted.replace(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>");
      formatted = formatted.replace(/\n/g, "<br>");

      return formatted;
    },
  },
};
</script>

<style scoped>
/* AI Chat Drawer Styles */
.ai-chat-drawer {
  box-shadow: -4px 0 20px rgba(0, 0, 0, 0.1) !important;
}

.chat-header {
  border-bottom: 1px solid rgba(0, 0, 0, 0.05);
}

.chat-messages-container {
  height: calc(100vh - 200px);
  overflow-y: auto;
  background: #fafafa;
}

.welcome-chat {
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
}

.quick-actions {
  display: flex;
  flex-direction: column;
  gap: 8px;
  max-width: 280px;
  margin: 0 auto;
}

.messages-list {
  padding-top: 24px;
  padding-bottom: 24px;
}

.message-bubble {
  max-width: 320px;
  padding: 12px 16px;
  border-radius: 16px;
  position: relative;
  word-wrap: break-word;
  line-height: 1.4;
}

.message-bubble.user {
  background: rgba(0, 0, 0, 0.87);
  color: white;
  border-bottom-right-radius: 4px;
}

.message-bubble.assistant {
  background: white;
  border: 1px solid rgba(0, 0, 0, 0.1);
  border-bottom-left-radius: 4px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
}

.message-time {
  font-size: 11px;
  opacity: 0.6;
  margin-top: 4px;
  font-weight: 400;
}

.typing-bubble {
  padding: 16px 20px;
  background: white;
  border: 1px solid rgba(0, 0, 0, 0.1);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
}

.typing-dots {
  display: flex;
  gap: 6px;
  align-items: center;
}

.typing-dots span {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: rgba(0, 0, 0, 0.4);
  animation: typing 1.4s infinite ease-in-out;
}

.typing-dots span:nth-child(1) {
  animation-delay: -0.32s;
}

.typing-dots span:nth-child(2) {
  animation-delay: -0.16s;
}

@keyframes typing {
  0%,
  80%,
  100% {
    transform: scale(0.8);
    opacity: 0.3;
  }
  40% {
    transform: scale(1);
    opacity: 1;
  }
}

.chat-input-area {
  border-top: 1px solid rgba(0, 0, 0, 0.05);
}

.chat-input-field >>> .v-input__slot {
  box-shadow: none !important;
  border: 1px solid rgba(0, 0, 0, 0.1) !important;
}

.chat-input-field >>> .v-input__slot:hover {
  border-color: rgba(0, 0, 0, 0.2) !important;
}

/* Floating Button */
.floating-chat-btn {
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.15) !important;
  transition: all 0.3s ease !important;
}

.floating-chat-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.2) !important;
}

/* Button Styles */
.v-btn {
  text-transform: none !important;
  font-weight: 500 !important;
  border-radius: 8px !important;
  letter-spacing: 0 !important;
}

/* Form Styles */
.v-text-field--outlined >>> .v-input__control > .v-input__slot {
  border-radius: 8px !important;
  border: 1px solid rgba(0, 0, 0, 0.1) !important;
}

.v-text-field--outlined:not(.v-input--is-focused):not(.v-input--has-state)
  >>> .v-input__slot:hover {
  border-color: rgba(0, 0, 0, 0.2) !important;
}

.v-text-field--outlined.v-input--is-focused >>> .v-input__slot {
  border-width: 2px !important;
}

/* Scrollbar Styles */
::-webkit-scrollbar {
  width: 6px;
  height: 6px;
}

::-webkit-scrollbar-track {
  background: transparent;
}

::-webkit-scrollbar-thumb {
  background: rgba(0, 0, 0, 0.1);
  border-radius: 3px;
}

::-webkit-scrollbar-thumb:hover {
  background: rgba(0, 0, 0, 0.2);
}

/* Responsive Design */
@media (max-width: 960px) {
  .message-bubble {
    max-width: 280px;
  }
  .floating-chat-btn {
    margin-right: 16px !important;
    margin-bottom: 16px !important;
  }
}

@media (max-width: 600px) {
  .ai-chat-drawer {
    width: 100% !important;
  }
  .message-bubble {
    max-width: 240px;
  }
}

/* Print styles */
@media print {
  .floating-chat-btn,
  .ai-chat-drawer {
    display: none !important;
  }
}
</style>
